const MAX_ENTRIES = 20;
const cache = new Map();
const keyOrder = [];
const LOCAL_STORAGE_PREFIX = "avf-table-settings:";

export const buildAssetViewFieldCacheKey = ({ clientId }) => String(clientId ?? "");

const readPersistedTableSettings = cacheKey => {
    if (!cacheKey) return null;
    try {
        const raw = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
};

const writePersistedTableSettings = (cacheKey, tableSettings) => {
    if (!cacheKey) return;
    try {
        if (tableSettings == null) {
            localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`);
            return;
        }
        localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`, JSON.stringify(tableSettings));
    } catch (_e) {
        // ignore quota / private mode errors
    }
};

export const mergeAssetViewTableSettings = (primary, fallback) => {
    if (!primary) return fallback ?? null;
    if (!fallback?.config) return primary;
    const mergedConfig = { ...(primary.config || {}) };
    Object.entries(fallback.config).forEach(([key, entry]) => {
        if (!entry || typeof entry !== "object") return;
        if (entry.isVisible === false || entry.isVisible === "false" || entry.isVisible === 0 || entry.isVisible === "0") {
            mergedConfig[key] = { ...(mergedConfig[key] || {}), ...entry, isVisible: false };
        }
    });
    const keys = Array.isArray(primary.keys)
        ? [...new Set([...primary.keys, ...(Array.isArray(fallback.keys) ? fallback.keys : [])])]
        : fallback.keys;
    return { ...primary, keys, config: mergedConfig };
};

export const getCachedAssetViewTableSettings = cacheKey => {
    if (!cacheKey || !cache.has(cacheKey)) return undefined;
    return cache.get(cacheKey);
};

export const setCachedAssetViewTableSettings = (cacheKey, tableSettings) => {
    if (!cacheKey) return;
    if (!cache.has(cacheKey)) {
        keyOrder.push(cacheKey);
        if (keyOrder.length > MAX_ENTRIES) {
            const evict = keyOrder.shift();
            cache.delete(evict);
            writePersistedTableSettings(evict, null);
        }
    }
    cache.set(cacheKey, tableSettings);
    writePersistedTableSettings(cacheKey, tableSettings);
};

export const getPersistedAssetViewTableSettings = cacheKey => readPersistedTableSettings(cacheKey);

export const invalidateAssetViewFieldCache = ({ clientId }) => {
    const cacheKey = buildAssetViewFieldCacheKey({ clientId });
    if (!cacheKey) return;
    cache.delete(cacheKey);
    writePersistedTableSettings(cacheKey, null);
    const idx = keyOrder.indexOf(cacheKey);
    if (idx >= 0) keyOrder.splice(idx, 1);
};

export const hasCachedAssetViewTableSettings = cacheKey => cacheKey != null && cache.has(cacheKey);

export const hasStoredAssetViewTableSettings = cacheKey =>
    hasCachedAssetViewTableSettings(cacheKey) || !!getPersistedAssetViewTableSettings(cacheKey);
